import type {
  InventoryListItem,
  ListMarketplaceListing,
  ListStockMismatchFinding,
  LocalStockState,
  MarketplacePresenceState,
  ProviderConnectionState,
  ProviderSlot,
  StockDisagreement,
} from '@/lib/api/schemas/inventory.schema'
import type { WarehouseProductStatus } from '@/lib/warehouse/labels'
import type { WarehouseSortColumn } from '@/lib/warehouse/url-state'

/**
 * Read-side projections over the almaz.product-graph/v1 payload. Nothing here
 * resolves identity, matches a source to a variant or writes a product fact:
 * every value is either taken from the payload or reported as unavailable.
 *
 * `attributes` and `identity` are guaranteed non-null by the contract, so both
 * are read directly. A fact the row cannot evidence arrives with its closed
 * unavailable reason rather than being absent or invented.
 */

export const PRESENCE_NOT_OBSERVED_REASON = 'presence.not_observed'

/**
 * What this row is called. Mirrors the server's `_article_key`
 * (`model_article or article or ""`) member for member, including
 * the empty tail: returning null instead would invert the ordering of a row
 * with neither article, which sorts first on the server and would sort last
 * here. Used for the column, the row's accessible name and the sort key so all
 * three agree with each other and with the page they arrived in.
 */
export function displayArticle(variant: InventoryListItem): string {
  return variant.modelArticle || variant.article || ''
}

export interface WarehouseSizeSortKey {
  numeric: number
  original: string
}

function compareBytes(left: string, right: string): number {
  const leftBytes = new TextEncoder().encode(left)
  const rightBytes = new TextEncoder().encode(right)
  const length = Math.min(leftBytes.length, rightBytes.length)
  for (let index = 0; index < length; index += 1) {
    const difference = leftBytes[index] - rightBytes[index]
    if (difference !== 0) return difference
  }
  return leftBytes.length - rightBytes.length
}

/** The numeric component and original spelling used by the size parity oracle. */
export function sizeSortKeyOf(size: string | null | undefined): WarehouseSizeSortKey | null {
  if (size === null || size === undefined) return null
  const component = size.split('\\', 1)[0].replace(',', '.')
  const match = /^(\d+(?:\.\d+)?)/.exec(component)
  if (match === null) return null
  const numeric = Number(match[1])
  return Number.isFinite(numeric) ? { numeric, original: size } : null
}

/** Reference ordering for parity coverage; no delivered page uses this comparator. */
export function compareWarehouseSizeSortKeys(
  left: WarehouseSizeSortKey | null,
  right: WarehouseSizeSortKey | null,
): number {
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  return left.numeric - right.numeric || compareBytes(left.original, right.original)
}

export function isUngrouped(variant: InventoryListItem): boolean {
  return variant.modelArticle === null
}

export function weightProvenance(variant: InventoryListItem): string {
  return variant.frozenRegistry
    ? `Зафиксированный реестр 1С · ${variant.frozenRegistry.sourceAdapterId}`
    : 'Каноническая запись'
}

export interface LocalStock {
  quantity: number | null
  unavailableReason: string | null
}

/**
 * Mirrors the server's `_local_stock`: the greatest effective available across
 * the 1C sources. Deriving it any other way lets the number in the column
 * contradict the `localStock` filter that selected the row. `effectiveAvailable`
 * is already net of a protective hold, so a held unit is not added back here.
 */
export function localStockOf(variant: InventoryListItem): LocalStock {
  const onec = variant.sources.filter((source) => source.sourceKind === 'onec')
  const quantities = onec
    .map((source) => source.effectiveAvailable)
    .filter((value): value is number => value !== null)
  if (quantities.length === 0) {
    const reason = onec.find((source) => source.freshnessReason)?.freshnessReason ?? 'inventory.stock_unavailable'
    return { quantity: null, unavailableReason: reason }
  }
  return { quantity: Math.max(...quantities), unavailableReason: null }
}

/** The same three-valued rule the server filters on. No observation is never zero. */
export function localStockStateOf(variant: InventoryListItem): LocalStockState {
  const { quantity } = localStockOf(variant)
  if (quantity === null) return 'unknown'
  return quantity > 0 ? 'in_stock' : 'out_of_stock'
}

export interface WarehouseActivity {
  at: string | null
  source: string | null
  grain: string | null
  unavailableReason: string | null
}

/**
 * The Product status projection is additive to the older variant payload.  The
 * transport schema is owned by the API contract; keeping this read-side shape
 * local lets an older browser remain compatible while consuming the published
 * nested value when it is present.
 */
type ProductActivityStatusValue =
  | 'processing'
  | 'delivery'
  | 'delivered'
  | 'cancelled'
  | 'returning'
  | 'returned'
  | 'delivery_issue'
  | 'in_stock'
  | 'reserved'
  | 'out_of_stock'
  | 'sold'
  | 'withdrawn'
  | 'unknown'

type ProductMovementSource =
  | 'order'
  | 'cdek_event'
  | 'onec_capture'
  | 'none'
  | 'carrier_event'
  | 'marketplace_presence'
  | 'conflict'

export const MOVEMENT_CONFLICT_REASON = 'status.movement_source_conflict'

/**
 * The Russian name of the observation the server said moved the item last. A
 * carrier the server has been configured for and one it has not both arrive as
 * `carrier_event`, so the browser never spells a carrier the server did not
 * name. `conflict` is deliberately not one of these: a tie has no single
 * source, and `movementSourceLabel` returning null is what says so.
 */
const movementSourceLabels: Partial<Record<ProductMovementSource, string>> = {
  order: 'Заказ',
  cdek_event: 'Событие доставки',
  carrier_event: 'Событие перевозчика',
  marketplace_presence: 'Наблюдение витрины',
}

const MOVEMENT_SOURCES_WITH_BUSINESS_CLOCK: ReadonlySet<ProductMovementSource> = new Set([
  'order',
  'cdek_event',
  'carrier_event',
  'marketplace_presence',
  'conflict',
])

interface ProductStatusPayload {
  status: ProductActivityStatusValue
  publishedStatus: WarehouseProductStatus
  movementAt: string | null
  movementSource: ProductMovementSource
  unavailableReason: string | null
}

interface ProductTimelinePayload {
  productStatus?: ProductStatusPayload | null
  firstSeenInOnecAt?: string | null
  firstSeenInOnecUnavailableReason?: string | null
  firstSeenAt?: string | null
  firstSeenAtUnavailableReason?: string | null
}

type ProductWithTimeline = InventoryListItem & ProductTimelinePayload

function hasProductTimeline(variant: InventoryListItem): variant is ProductWithTimeline {
  return 'productStatus' in variant || 'firstSeenInOnecAt' in variant || 'firstSeenAt' in variant
}

function productStatusPayloadOf(variant: InventoryListItem): ProductStatusPayload | null {
  if (!hasProductTimeline(variant)) return null
  return variant.productStatus ?? null
}

export function productUinOf(variant: InventoryListItem): string | null {
  if (typeof variant.uin !== 'string') return null
  return variant.uin
}

const ACTIVITY_MOVEMENT_KINDS: ReadonlySet<InventoryListItem['movements'][number]['kind']> = new Set([
  'marketplace_order',
  'onec_offline_sale',
  'onec_reservation',
])

/** Whether the server refused to rank two observations sharing one instant. */
export function hasMovementConflict(variant: InventoryListItem): boolean {
  const projection = productStatusPayloadOf(variant)
  if (projection === null) return false
  return projection.movementSource === 'conflict'
    || projection.unavailableReason === MOVEMENT_CONFLICT_REASON
}

/**
 * The server's published status is the only source for the per-item state.
 * `currentState` is deliberately NOT a fallback: it is the state 1C recorded,
 * which is one input to that answer and loses to a proven order attachment.
 * Reading it here would put a second state on the row, and the row would then
 * contradict the filter that no longer selects over it. A payload carrying no
 * published status is explicitly unknown, under its own reason.
 */
export function productStatusOf(variant: InventoryListItem): { status: WarehouseProductStatus; reason: string | null } {
  const projection = productStatusPayloadOf(variant)
  if (projection !== null) {
    // The server's own published member, rendered and never re-decided here.
    // The browser used to translate the projection's evidence vocabulary into
    // this one while the filter selected over a different fact entirely, which
    // is how the column and the filter came to disagree.
    return { status: projection.publishedStatus, reason: projection.unavailableReason }
  }
  // Stock disagreement and reconciliation review are independent evidence
  // flags. They must not replace the Product-owned state: doing so turns a
  // missing state into a client-invented conflict and makes the unavailable
  // reason disappear. The review column renders those flags separately.
  return { status: 'unknown', reason: variant.currentStateUnavailableReason ?? 'inventory.product_state_unavailable' }
}

function movementActivity(variant: InventoryListItem): WarehouseActivity {
  const latest = variant.movements
    .filter((movement) => ACTIVITY_MOVEMENT_KINDS.has(movement.kind))
    .sort((left, right) => right.occurredAt.localeCompare(left.occurredAt))[0]
  return latest === undefined
    ? { at: null, source: null, grain: null, unavailableReason: 'inventory.activity_not_recorded' }
    : { at: latest.occurredAt, source: latest.sourceAccountCode, grain: 'product', unavailableReason: null }
}

/** The observation the server named, or null when a tie left it undecided. */
export function movementSourceLabel(variant: InventoryListItem): string | null {
  const projection = productStatusPayloadOf(variant)
  if (projection === null) return null
  return movementSourceLabels[projection.movementSource] ?? null
}

/** Product-projected business activity, with an explicit null reason. */
/** Business movement only; a 1C capture timestamp is a separate fact. */
export function businessActivityOf(variant: InventoryListItem): WarehouseActivity {
  const projection = productStatusPayloadOf(variant)
  if (projection !== null) {
    if (projection.movementAt !== null && MOVEMENT_SOURCES_WITH_BUSINESS_CLOCK.has(projection.movementSource)) {
      // A tie keeps its instant — both observations agree on when — and loses
      // only its single source, which the conflict reason then explains.
      return {
        at: projection.movementAt,
        source: movementSourceLabels[projection.movementSource] ?? null,
        grain: 'product',
        unavailableReason: projection.unavailableReason,
      }
    }
    return {
      at: null,
      source: null,
      grain: null,
      unavailableReason: projection.unavailableReason ?? 'inventory.activity_not_recorded',
    }
  }
  if (variant.lastActivityAt !== null) {
    return {
      at: variant.lastActivityAt,
      source: variant.lastActivitySource,
      grain: variant.lastActivityGrain,
      unavailableReason: null,
    }
  }
  if (variant.lastActivityUnavailableReason !== null) {
    return {
      at: null,
      source: null,
      grain: null,
      unavailableReason: variant.lastActivityUnavailableReason,
    }
  }
  return movementActivity(variant)
}

/** A genuine first-seen-in-1C fact, when the server explicitly publishes one. */
export function firstSeenInOnecOf(variant: InventoryListItem): WarehouseActivity {
  if (!hasProductTimeline(variant)) {
    return {
      at: null,
      source: null,
      grain: null,
      unavailableReason: 'inventory.first_seen_in_onec_unavailable',
    }
  }
  const firstSeenAt = variant.firstSeenInOnecAt !== undefined ? variant.firstSeenInOnecAt : variant.firstSeenAt
  if (firstSeenAt !== undefined) {
    return firstSeenAt === null
      ? {
          at: null,
          source: null,
          grain: null,
          unavailableReason: variant.firstSeenInOnecUnavailableReason
            ?? variant.firstSeenAtUnavailableReason
            ?? 'inventory.first_seen_in_onec_unavailable',
        }
      : {
          at: firstSeenAt,
          source: '1С',
          grain: 'product',
          unavailableReason: null,
        }
  }
  return {
    at: null,
    source: null,
    grain: null,
    unavailableReason: 'inventory.first_seen_in_onec_unavailable',
  }
}

/**
 * Presents a business movement when one exists, otherwise a separately
 * labelled first-seen-in-1C fact. The fallback is never used as a movement
 * sort key: a first-seen clock is evidence of capture, not business activity.
 */
export function lastActivityOf(variant: InventoryListItem): WarehouseActivity {
  const activity = businessActivityOf(variant)
  if (activity.at !== null) return activity
  const firstSeen = firstSeenInOnecOf(variant)
  return firstSeen.at === null ? activity : { ...firstSeen, source: 'Впервые в 1С' }
}

function latestTimestamp(values: readonly string[]): string | null {
  return values.reduce<string | null>((latest, value) => {
    if (latest === null) return value
    return Date.parse(value) > Date.parse(latest) ? value : latest
  }, null)
}

export interface WarehouseReconciliationDate {
  at: string | null
  unavailableReason: string | null
}

/**
 * Uses only active 1C observation/extraction evidence.  Product status's
 * `onec_capture` clock is also accepted here because it is explicitly typed as
 * the active capture; it is never returned by `lastActivityOf` as movement.
 */
export function reconciliationDateOf(variant: InventoryListItem): WarehouseReconciliationDate {
  const projection = productStatusPayloadOf(variant)
  if (projection?.movementSource === 'onec_capture' && projection.movementAt !== null) {
    return { at: projection.movementAt, unavailableReason: null }
  }
  const observedAt = latestTimestamp(
    variant.sources
      .filter((source) => source.sourceKind === 'onec' && source.isActive)
      .flatMap((source) => source.warehouses.map((warehouse) => warehouse.observedAt)),
  )
  return observedAt === null
    ? { at: null, unavailableReason: 'inventory.reconciliation_date_unavailable' }
    : { at: observedAt, unavailableReason: null }
}

export function warehouseDateOnly(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short' }).format(new Date(value))
}

/** A stable dated/unavailable bucket and Product identity tie-break. */
export function activitySortValueOf(variant: InventoryListItem): string {
  const activity = businessActivityOf(variant)
  return `${activity.at === null ? '1' : '0'}:${activity.at ?? ''}:${variant.productId}`
}

export interface MarketplaceBadge {
  providerCode: string
  accountCode: string | null
  displayName: string
  connectionState: ProviderConnectionState
  presence: MarketplacePresenceState
  observedAt: string | null
  unavailableReason: string | null
}

/**
 * Which provider columns exist, always from the server: the page's declared
 * slots, else the per-variant listings, which carry one row per declared slot
 * on both the list and the detail route. A provider the server never names is
 * not rendered, so the UI cannot imply a channel that does not exist.
 */
export function declaredProviderSlots(
  variant: InventoryListItem,
  providerSlots: readonly ProviderSlot[] | null | undefined,
): ProviderSlot[] {
  if (providerSlots?.length) return [...providerSlots]
  return (variant.listings ?? []).map((listing) => ({
    providerCode: listing.providerCode,
    accountCode: listing.accountCode,
    displayName: listing.displayName,
    connectionState: listing.connectionState,
    unavailableReason: listing.unavailableReason,
    placementObserved: false,
  }))
}

/**
 * The storefronts the shop has, in the owner's order, always drawn.
 *
 * A storefront the server declares takes the server's state. One it does not
 * declare is switched off, never absent. ORI has no provider row by FORGE-D14,
 * so it is always switched off and never read from the server.
 */
export const STOREFRONT_SLOTS = [
  { providerCode: 'wildberries', displayName: 'Wildberries', serverDeclared: true },
  { providerCode: 'ozon', displayName: 'Ozon', serverDeclared: true },
  { providerCode: 'ymarket', displayName: 'Яндекс Маркет', serverDeclared: true },
  { providerCode: 'ori', displayName: 'ORI', serverDeclared: false },
] as const

function switchedOffStorefront(providerCode: string, displayName: string): MarketplaceBadge {
  return { providerCode, accountCode: null, displayName, connectionState: 'not_connected', presence: 'unknown', observedAt: null, unavailableReason: null }
}

export function marketplaceBadges(
  variant: InventoryListItem,
  providerSlots: readonly ProviderSlot[] | null | undefined,
): MarketplaceBadge[] {
  const declared = declaredBadges(variant, providerSlots)
  return STOREFRONT_SLOTS.map((slot) => {
    const badge = slot.serverDeclared ? declared.find((entry) => entry.providerCode === slot.providerCode) : undefined
    // A listing's display name is its account code spelled out («wildberries almaz»); the owner reads the storefront.
    return badge === undefined ? switchedOffStorefront(slot.providerCode, slot.displayName) : { ...badge, displayName: slot.displayName }
  })
}

function declaredBadges(
  variant: InventoryListItem,
  providerSlots: readonly ProviderSlot[] | null | undefined,
): MarketplaceBadge[] {
  const presenceByAccount = new Map((variant.marketplacePresence ?? []).map((row) => [row.accountCode, row]))
  const listings = variant.listings ?? []

  return declaredProviderSlots(variant, providerSlots).map((slot) => {
    const listing = listings.find((row) => row.providerCode === slot.providerCode
      && (slot.accountCode === null || row.accountCode === slot.accountCode))
    const accountCode = listing?.accountCode ?? slot.accountCode
    const observed = accountCode === null ? undefined : presenceByAccount.get(accountCode)
    const connectionState = listing?.connectionState ?? slot.connectionState
    const presence: MarketplacePresenceState = connectionState === 'not_connected'
      ? 'unknown'
      : observed?.presence ?? 'unknown'
    return {
      providerCode: slot.providerCode,
      accountCode,
      displayName: listing?.displayName ?? slot.displayName,
      connectionState,
      presence,
      observedAt: observed?.observedAt ?? listing?.observedAt ?? null,
      unavailableReason: listing?.unavailableReason
        ?? slot.unavailableReason
        ?? (presence === 'unknown' && connectionState !== 'not_connected' ? PRESENCE_NOT_OBSERVED_REASON : null),
    }
  })
}

/** Always an array on the wire; empty means the server declared no provider. */
export function listingsOf<L extends ListMarketplaceListing>(
  variant: { listings?: readonly L[] | null },
): L[] {
  return [...(variant.listings ?? [])]
}

/** Only a row whose two quantities are both real numbers and differ. */
export function stockDisagreementsOf(variant: InventoryListItem): StockDisagreement[] {
  return (variant.stockDisagreements ?? []).filter((row) => (
    row.canonicalQuantity !== null && row.observedQuantity !== null && row.canonicalQuantity !== row.observedQuantity
  ))
}

export function stockMismatchFindingsOf<F extends ListStockMismatchFinding>(
  variant: { stockMismatchFindings?: readonly F[] | null },
): F[] {
  return [...(variant.stockMismatchFindings ?? [])]
}

export function hasStockMismatch(variant: InventoryListItem): boolean {
  if (stockMismatchFindingsOf(variant).length > 0) return true
  if (variant.stockDisagreements) return stockDisagreementsOf(variant).length > 0
  return variant.discrepancies.some((row) => row.reason === 'stock_divergence')
}

export function sortValueOf(variant: InventoryListItem, column: WarehouseSortColumn): string | number | null {
  switch (column) {
    // UIN and lifecycle exist only at physical-unit grain. This legacy
    // variant projection is not wired into DataTable sorting; the server owns
    // the order for every member of WarehouseSortColumn.
    case 'uin': return null
    case 'article': return displayArticle(variant)
    case 'title': return variant.title
    case 'weight': return variant.attributes.netWeightGrams
    case 'lifecycle': return null
    case 'activity': return activitySortValueOf(variant)
    case 'size': return null
    case 'photoCount': return null
  }
  return null
}
